'use client';

import { usePathname } from "next/navigation";
import { useMemo } from "react";

// 인증이 필요한 경로 목록
const AUTHENTICATED_ROUTES = [
  '/dashboard',
  '/chat',
  '/sign-in',
  '/sign-up',
  '/api',
  '/settings',
  '/agents',
];

/**
 * 경로가 공개 모드인지 확인하는 함수
 * - /expo/* 경로: 공개 모드
 * - /{slug} 경로 (단일 세그먼트, 알려진 인증 경로가 아닌 경우): 공개 모드
 * - 그 외 경로: 인증 모드
 */
export function isPublicPath(pathname: string | null): boolean {
  // pathname이 없으면 안전하게 공개 모드로 처리 (에러 방지)
  if (!pathname) return true;

  // /expo/* 경로는 항상 공개 모드
  if (pathname.startsWith('/expo')) return true;

  // 루트 경로는 인증 필요
  if (pathname === '/') return false;

  // 인증이 필요한 경로인지 확인
  const isAuthenticatedRoute = AUTHENTICATED_ROUTES.some(route =>
    pathname.startsWith(route)
  );

  if (isAuthenticatedRoute) return false;

  // 단일 세그먼트 경로 (/{slug} 형태)면 공개 모드
  // 예: /my-ai-bot, /ne_faq 등
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 1) {
    return true;
  }

  return false;
}

// 공개 모드 반환값 - 모듈 레벨 상수 (SSR/CSR 일관성 보장)
export const PUBLIC_AUTH_STATE = {
  userId: 'public_expo_user' as string | null,
  isPublicMode: true,
  isLoaded: true,
  isSignedIn: false,
} as const;

// 인증 모드 기본값 - 모듈 레벨 상수
const AUTH_MODE_DEFAULT_STATE = {
  userId: null as string | null,
  isPublicMode: false,
  isLoaded: true,
  isSignedIn: false,
} as const;

export type AuthState = {
  userId: string | null;
  isPublicMode: boolean;
  isLoaded: boolean;
  isSignedIn: boolean;
};

/**
 * 공개/인증 모드를 자동 감지하는 훅
 *
 * CRITICAL: React 훅 규칙 준수
 * - 이 훅은 조건부 훅 호출을 하지 않음
 * - 항상 동일한 순서로 훅을 호출함
 * - 공개 경로에서는 Clerk를 절대 로드하지 않음
 *
 * 사용 방법:
 * - 공개 페이지 (/expo/*, /{slug}): 자동으로 공개 모드 반환
 * - 인증 페이지: 기본 인증 상태 반환 (실제 Clerk 인증은 ClerkProvider에서 처리)
 *
 * @returns {AuthState} 인증 정보
 */
export function useOptionalAuth(): AuthState {
  const pathname = usePathname();

  // useMemo로 isPublicMode 계산 (pathname 변경시에만 재계산)
  const isPublicMode = useMemo(() => isPublicPath(pathname), [pathname]);

  // 훅 호출 없이 조건부 반환 - React 규칙 준수
  // 중요: 여기서 다른 훅을 호출하지 않음
  if (isPublicMode) {
    return PUBLIC_AUTH_STATE;
  }

  // 인증 모드에서는 기본 상태 반환
  // 실제 Clerk 인증은 (auth) 라우트 그룹의 ClerkProvider에서 처리됨
  // 이 훅은 단순히 공개/인증 모드 판별만 수행
  return AUTH_MODE_DEFAULT_STATE;
}

/**
 * 공개 모드 전용 훅 - 항상 공개 상태 반환
 * 공개 페이지에서 useOptionalAuth 대신 사용 가능
 */
export function usePublicAuth(): AuthState {
  return PUBLIC_AUTH_STATE;
}
